import {useMemo, useEffect, useRef, useState} from 'react'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useQuery, UseQueryResult} from '@tanstack/react-query'
import {useCookies} from 'react-cookie'
import {useIntl} from 'react-intl'
import {CategoryType, ProductSearchType, PageDesignerType, ProductSearchHits} from '_types'
import {useGlobalConfigState} from '_context'
import {getAbsoluteUrl} from '_utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {API} from '_customHooks'
import type {
    CustomSdkData,
    ProductSearchHit,
    DySearchParams,
    DySearchRequestBody,
    DySearchResponse,
    DySlot,
    DyFacet,
    DyCookieMap,
    DyCookie,
    DyApiResponse,
    PageIdWithUrlSegment,
    CustomSdkHookParams,
} from '../../types/dynamic-yield'

async function fetchAndMergeProducts(
    params: DySearchParams,
    locale: {id?: string; preferredCurrency?: string; alias?: string} | null,
    categoryId: string | null,
    parentCategoryTree: unknown,
    siteId: string,
    categoryData: CategoryType | null,
    dyCookies: DyCookieMap,
    consentAccepted: boolean,
    setCookie: (name: string, value: string, options: {path: string; maxAge: number}) => void,
    intl?: {formatMessage: (msg: {id: string; defaultMessage: string}) => string},
    hostLinkLocalized?: string | null,
): Promise<DySearchResponse> {
    const limit = params.limit ?? 0
    let totalToFetch = params.offset ?? 0

    // Prima richiesta per ottenere il totale disponibile
    const firstResponse = await dynamicYieldProductSearch(
        {...params, offset: 0, limit},
        locale,
        categoryId,
        parentCategoryTree,
        siteId,
        categoryData,
        dyCookies,
        consentAccepted,
        setCookie,
        intl,
        hostLinkLocalized,
    )

    const totalAvailable = firstResponse?.total ?? 0
    // Limita totalToFetch al massimo al totale disponibile
    totalToFetch = Math.min(totalToFetch, totalAvailable)

    let mergedHits: ProductSearchHit[] = []
    const mergedResponse: DySearchResponse = {...firstResponse}

    const productsFetched = firstResponse?.hits?.length ?? 0

    if (firstResponse?.hits) mergedHits = [...firstResponse.hits]

    // Calcola quante richieste parallele servono
    const remainingToFetch = totalToFetch - productsFetched
    if (remainingToFetch > 0) {
        const parallelRequests: Array<Promise<DySearchResponse>> = []
        let currentOffset = productsFetched

        while (currentOffset < totalToFetch) {
            const currentLimit = Math.min(limit, totalToFetch - currentOffset)
            parallelRequests.push(
                dynamicYieldProductSearch(
                    {...params, offset: currentOffset, limit: currentLimit},
                    locale,
                    categoryId,
                    parentCategoryTree,
                    siteId,
                    categoryData,
                    dyCookies,
                    consentAccepted,
                    setCookie,
                    intl,
                    hostLinkLocalized,
                ),
            )

            currentOffset += currentLimit
        }

        // Esegui tutte le richieste in parallelo
        const parallelResponses = await Promise.all(parallelRequests)

        // Unisci tutti i risultati nell'ordine corretto
        parallelResponses.forEach((response) => {
            if (response?.hits) {
                mergedHits = [...mergedHits, ...response.hits]
            }
        })
    }

    if (mergedResponse) mergedResponse.hits = mergedHits.slice(0, totalToFetch)

    return mergedResponse
}

// Prepara i filtri per la query di Dynamic Yield
function buildDynamicYieldFilters(params: DySearchParams, categoryId: string | null) {
    const filters: Array<{field: string; values?: string[]; min?: number}> = []
    const groupedValues = new Map<string, Set<string>>()
    // Aggiungi filtro categoria se fornito
    if (categoryId) {
        filters.push({
            field: 'categories',
            values: [categoryId],
        })
    }

    // Parsing dei refinement filters da params.refine
    if (params.refine && Array.isArray(params.refine)) {
        params.refine.forEach((refinement: string) => {
            const [field, ...valueParts] = refinement.split('=')
            const rawValue = valueParts.join('=')
            if (!field || !rawValue || field === 'cgid' || field === 'pmid' || field === 'price') return

            const values = rawValue
                .split('|')
                .map((val) => val.trim())
                .filter(Boolean)
            if (!groupedValues.has(field)) {
                groupedValues.set(field, new Set())
            }
            const valueSet = groupedValues.get(field)
            values.forEach((val) => valueSet?.add(val))
        })
    }

    groupedValues.forEach((values, field) => {
        filters.push({
            field,
            values: Array.from(values),
        })
    })

    return filters
}

// Converte il parametro sort al formato sortBy di Dynamic Yield
function buildSortBy(sortParam?: string) {
    if (!sortParam) return null
    // Mappatura dei sort ID ai field e order di Dynamic Yield
    const sortMappings = {
        'price-ascending': {field: 'price', order: 'asc'},
        'price-descending': {field: 'price', order: 'desc'},
        'Online Timestamp': {field: 'type:number:OnlineTimestamp', order: 'desc'},
        // 'best-matches': {field: 'popularity', order: 'asc'}, per il momento rimuoviamo perchè non sappiamo se sovrascrive le experience
    }

    return sortMappings[sortParam] || null
}

// Costruisce il request body per la chiamata a Dynamic Yield
function buildDynamicYieldRequestBody(
    params: DySearchParams,
    locale: {id?: string; preferredCurrency?: string} | null,
    categoryId: string | null,
    parentCategoryTree: unknown,
    dyCookies: DyCookieMap,
    consentAccepted: boolean,
    hostLinkLocalized?: string | null,
): DySearchRequestBody {
    const filters = buildDynamicYieldFilters(params, categoryId)
    const sortBy = buildSortBy(params.sort)

    // Costruisci l'array dei field filters
    const fieldFilters = [
        'price',
        'image_url',
        'name',
        'categories',
        'in_stock',
        'image_url_1',
        'image_url_2',
        'image_url_3',
        'image_url_4',
        'image_url_5',
        'rangePrice',
        'gender',
        'categoryPath',
        'color',
        'shortDescription',
        'colorQuantity',
        'videoV',
        'videoH',
        'has3dTag',
        'alt_image',
        'url',
        'group_id',
        'textTag',
        'type',
        'coverImageTileUrl',
    ]

    // Costruisci l'oggetto query
    const query: {
        text: string
        filters: Array<{field: string; values?: string[]; min?: number}>
        pagination: {offset: number; numItems: number}
        sortBy?: {field: string; order: string}
    } = {
        text: params.q || '',
        filters: filters,
        pagination: {
            offset: params.offset || 0,
            numItems: params.limit || 12,
        },
    }

    // Aggiungi sortBy solo se presente
    if (sortBy) {
        query.sortBy = sortBy
    }

    // Build user object with DY cookies if present
    const user: {
        active_consent_accepted: boolean
        dyid_server?: string
        dyid?: string
    } = {
        active_consent_accepted: Boolean(consentAccepted),
    }

    if (dyCookies._dyid_server) {
        user.dyid_server = String(dyCookies._dyid_server)
    }
    if (dyCookies._dyid) {
        user.dyid = String(dyCookies._dyid)
    }
    const session: {
        dy?: string
    } = {
        dy: dyCookies._dyjsession ? String(dyCookies._dyjsession) : undefined,
    }

    return {
        user: user,
        session: session,
        query: query,
        context: {
            page: {
                type: 'CATEGORY',
                location: hostLinkLocalized ? hostLinkLocalized : 'shop.brunellocucinelli.com',
                locale: locale?.id?.replace('-', '_') || 'en_IT',
                data: [categoryId],
            },
        },
        selector: {
            name: 'Semantic Search',
        },
        options: {
            returnAnalyticsMetadata: true,
            isImplicitClientData: false,
            productData: {
                fieldFilter: fieldFilters,
            },
        },
    }
}

// Helper function per estrarre il parametro type dall'URL e rimuoverlo
function extractAndRemoveTypeParam(url: string): {cleanUrl: string; type: string | null} {
    if (!url) return {cleanUrl: url, type: null}

    const urlObj = new URL(url, 'https://dummy.com')
    const typeParam = urlObj.searchParams.get('type')

    if (typeParam) {
        urlObj.searchParams.delete('type')
        const cleanUrl = urlObj.toString().replace('https://dummy.com', '')
        return {cleanUrl, type: typeParam}
    }

    return {cleanUrl: url, type: null}
}

function parseColorValue(colorValue: string | undefined): {label: string; code: string} | null {
    if (!colorValue) return null
    const parsed = JSON.parse(colorValue)
    if (parsed && typeof parsed === 'object') {
        return {
            label: typeof parsed.label === 'string' ? parsed.label : '',
            code: typeof parsed.code === 'string' ? parsed.code : '',
        }
    }
    return null
}

// Trasforma i prodotti da Dynamic Yield al formato Commerce Cloud
function transformDynamicYieldProducts(
    slots: DySlot[],
    params: DySearchParams,
    locale: {id?: string; alias?: string; preferredCurrency?: string} | null,
    hostLinkLocalized?: string | null,
    carouselDisabled?: boolean,
): ProductSearchHit[] {
    const imageTypes = ['F', 'R', 'D', 'K']
    const currencyCode = locale?.preferredCurrency || 'EUR'

    // Helper per formattare il prezzo con la valuta corretta
    const formatPrice = (price: number) => {
        if (!price && price !== 0) return ''
        // Mappa dei simboli di valuta
        const currencySymbols = {
            EUR: '€',
            USD: '$',
            GBP: '£',
            JPY: '¥',
            CNY: '¥',
            KRW: '₩',
            RUB: '₽',
            CAD: 'CA$',
            AUD: 'A$',
            CHF: 'CHF',
            HKD: 'HK$',
            SGD: 'S$',
            DKK: 'kr',
            QAR: 'ر.ق',
            SAR: 'ر.س',
            AED: 'د.إ',
            KWD: 'د.ك',
        }
        const symbol = currencySymbols[currencyCode] || currencyCode
        // Formatta il numero: migliaia con punti, decimali con virgola
        const parts = price.toFixed(2).split('.')
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        const formattedPrice = `${integerPart},${parts[1]}`
        return `${symbol} ${formattedPrice}`
    }

    return slots.map((slot) => {
        const productData = slot.productData || {}
        const sku = (productData.group_id || slot.sku || '').toString()

        // Ottieni i campi localizzati
        const shortDescription = productData['shortDescription'] || ''
        const altImage = productData['alt_image'] || ''
        const rawProductUrl = productData['url'] || ''
        const normalizedProductUrl = rawProductUrl.replace(/^\/+/, '')
        const aliasLower = (locale?.alias || '').toLowerCase()
        let productPath = normalizedProductUrl
        if (aliasLower) {
            if (productPath === aliasLower) {
                productPath = ''
            } else if (productPath.startsWith(`${aliasLower}/`)) {
                productPath = productPath.slice(aliasLower.length + 1)
            }
        }
        const relativePath = productPath ? `/${productPath}` : '/'
        const productUrl = hostLinkLocalized && aliasLower ? getAbsoluteUrl(hostLinkLocalized, aliasLower, relativePath) : normalizedProductUrl
        const textTag = productData['textTag'] || ''
        const parsedColor = parseColorValue(productData.color)
        const baseProductId = (() => {
            const groupId = typeof productData.group_id === 'string' ? productData.group_id : sku
            if (!groupId) return ''
            const colorCode = parsedColor?.code
            if (colorCode && groupId.includes(colorCode)) {
                return groupId.replace(colorCode, '')
            }
            return groupId
        })()

        // Costruisci array delle immagini
        const images: Array<{url: string; link: string; imageViewType: string; alt: string; title: string}> = []
        if (typeof productData.image_url === 'string') {
            const {cleanUrl, type} = extractAndRemoveTypeParam(productData.image_url)
            images.push({
                url: cleanUrl,
                link: cleanUrl,
                imageViewType: type || imageTypes[0],
                alt: altImage || productData.name || sku,
                title: productData.name || sku,
            })
        }
        for (let i = 1; i <= 5; i++) {
            const imageKey = `image_url_${i}`
            const imageValue = productData[imageKey]
            if (typeof imageValue === 'string' && imageValue.trim()) {
                const {cleanUrl, type} = extractAndRemoveTypeParam(imageValue)
                images.push({
                    url: cleanUrl,
                    link: cleanUrl,
                    imageViewType: type || imageTypes[i] || `${i}`,
                    alt: altImage || productData.name || sku,
                    title: productData.name || sku,
                })
            }
        }
        const hasPriceRange = !!productData.rangePrice

        return {
            currency: currencyCode,
            hitType: productData.type === 'ProductSet' ? 'set' : 'variation_group',
            image: productData.image_url
                ? {
                      alt: altImage || productData.name || sku,
                      link: productData.image_url,
                      title: productData.name || sku,
                  }
                : undefined,
            orderable: true,
            price: productData.price || 0,
            pricePerUnit: productData.price || 0,
            productId: sku,
            productName: productData.name || sku,
            productType: {
                variationGroup: productData.type !== 'ProductSet',
            },
            c_analytics: {
                productId: baseProductId,
                categoryPath: productData.categoryPath || '',
                season: productData.season || '',
                inStock: true,
                gender: productData.gender || '',
                shortDescription: shortDescription || '',
                color: parsedColor || undefined,
            },
            c_price: {
                currency: currencyCode,
                price: {
                    value: productData.price || 0,
                    formattedValue: formatPrice(productData.price || 0),
                },
                hasPriceRange: hasPriceRange,
                hasDiscount: false,
                priceRange: hasPriceRange && productData.rangePrice ? productData.rangePrice : null,
                discount: null,
            },
            c_images:
                images.length > 0
                    ? {
                          large: images,
                          cover: productData.coverImageTileUrl || null,
                          horizontal: null,
                          showCarousel: !productData.videoV && !productData.videoH && !productData.coverImageTileUrl && !carouselDisabled,
                      }
                    : undefined,
            c_videos: {
                vertical: productData.videoV || null,
                horizontal: productData.videoH || null,
            },
            c_shortDescription: shortDescription || '',
            c_has3dTag: productData.has3dTag || false,
            c_productUrl: productUrl || '',
            c_colorsQuantity: productData.colorQuantity || 1,
            c_ariaLabel: productData.name || sku,
            c_textTag: textTag || '',
        }
    })
}

// Capitalizza la prima lettera di ogni parola
function formatValueLabel(value: string): string {
    if (!value) return value
    // Gestisci "one size" e altri casi con spazi
    return value
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

// Trasforma i facets di Dynamic Yield in refinements di Commerce Cloud
function transformDynamicYieldFacets(
    facets: DyFacet[] | undefined,
    categoryData: CategoryType | null = null,
    intl: {formatMessage: (msg: {id: string; defaultMessage: string}) => string} | null = null,
) {
    if (!facets || !Array.isArray(facets)) return []

    const oneSizeLabel = intl ? intl.formatMessage({id: 'filter.label.one_size', defaultMessage: 'One Size'}) : 'One Size'

    const refinements: Array<{attributeId: string; label: string; values: Array<{hitCount?: number; label: string; value: string}>}> = []

    // Messaggi statici per l'estrazione
    const messages = {
        category: {id: 'filter.label.category', defaultMessage: 'Category'},
        color: {id: 'filter.label.color', defaultMessage: 'Color'},
        season: {id: 'filter.label.season', defaultMessage: 'Collection'},
        size: {id: 'filter.label.size', defaultMessage: 'Size'},
        line: {id: 'filter.label.line', defaultMessage: 'Line'},
    }

    // Mappa dei display values per i filtri
    const displayValueMap = {
        microcategoryacme: 'category',
        macrocategoryacme: 'category',
        'macro-color': 'color',
        editorialSeason: 'season',
        size: 'size',
        line: 'line',
    }

    // mostrare solo il filtro di tipo line solo per le seguenti categorie
    // questo perchè DY non permette di agganciare il filtro direttamente sulla categoria
    const allowedCategoriesForLineFilter = ['new-arrivals']
    const categoryId = categoryData?.id || ''

    // Determina quale categoria filtrare in base all'attributo della categoria
    const shouldFilterMicro = categoryData?.c_showCollectionCategoriesAsCategoryFilters
    const categoryToExclude = shouldFilterMicro ? 'microcategoryacme' : 'macrocategoryacme'

    facets.forEach((facet) => {
        // Salta i facet numerici (es: price con min/max) e categories
        if (facet.valuesType === 'number' || !facet.values || facet.values.length === 0 || facet.column === 'categories') {
            return
        }

        const facetColumn = facet.column || ''

        // Salta microcategoryacme o macrocategoryacme in base all'attributo della categoria
        if (facetColumn === categoryToExclude) {
            return
        }

        // Mostra il filtro line solo per le categorie permesse
        if (facetColumn === 'line' && !allowedCategoriesForLineFilter.includes(categoryId)) {
            return
        }

        // Ottieni il display value dalla mappa o usa il nome formattato come fallback
        const messageKey = displayValueMap[facetColumn]
        const displayValue = messageKey && intl ? intl.formatMessage(messages[messageKey]) : facet.displayName || formatValueLabel(facetColumn)

        const refinement = {
            attributeId: facetColumn, // Mantieni l'attributeId originale con il suffisso lingua
            label: displayValue,
            values: facet.values.map((v) => {
                const rawName = v.name ?? ''
                const rawValue = v.value ?? v.name ?? ''

                const resolvedValue = String(rawName || rawValue)
                const normalizedValue = resolvedValue.trim().toLowerCase()
                const label = facetColumn === 'size' ? (normalizedValue === 'os' ? oneSizeLabel : resolvedValue.toUpperCase()) : formatValueLabel(resolvedValue)

                return {
                    hitCount: v.count,
                    label,
                    value: resolvedValue,
                }
            }),
        }

        refinements.push(refinement)
    })
    return refinements
}

// Esegue la chiamata HTTP a Dynamic Yield
async function callDynamicYieldAPI(
    requestBody: DySearchRequestBody,
    siteId: string,
    setCookie: (name: string, value: string, options: {path: string; maxAge: number}) => void,
    consentAccepted: boolean,
): Promise<DyApiResponse> {
    const url = '/dySearch'
    const headers: Record<string, string> = {
        accept: 'application/json',
        'content-type': 'application/json',
    }

    const body = typeof window !== 'undefined' ? JSON.stringify({requestBody, siteId}) : JSON.stringify(requestBody)

    const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
    })

    if (!response.ok) {
        throw new Error(`Dynamic Yield API error: ${response.status} ${response.statusText}`)
    }
    const data = await response.json()
    // Handle cookies returned by Dynamic Yield
    if (consentAccepted && data.cookies && Array.isArray(data.cookies) && typeof window !== 'undefined') {
        data.cookies.forEach((cookie: DyCookie) => {
            const maxAgeSeconds = parseInt(cookie.maxAge, 10)
            setCookie(cookie.name, cookie.value, {
                path: '/',
                maxAge: maxAgeSeconds,
            })
        })
    }

    return data
}

// Funzione principale per la ricerca prodotti con Dynamic Yield
async function dynamicYieldProductSearch(
    params: DySearchParams,
    locale: {id?: string; preferredCurrency?: string} | null,
    categoryId: string | null = null,
    parentCategoryTree: unknown,
    siteId: string,
    categoryData: CategoryType | null = null,
    dyCookies: DyCookieMap,
    consentAccepted: boolean,
    setCookie: (name: string, value: string, options: {path: string; maxAge: number}) => void,
    intl?: {formatMessage: (msg: {id: string; defaultMessage: string}) => string},
    hostLinkLocalized?: string | null,
): Promise<DySearchResponse> {
    try {
        const requestBody = buildDynamicYieldRequestBody(params, locale, categoryId, parentCategoryTree, dyCookies, consentAccepted, hostLinkLocalized)
        const data = await callDynamicYieldAPI(requestBody, siteId, setCookie, consentAccepted)

        const choice = data.choices?.[0]
        const variation = choice?.variations?.[0]
        const analyticsMetadata = variation?.analyticsMetadata
        const decisionId = choice?.decisionId
        const analyticsMetadataWithDecision = analyticsMetadata || decisionId ? {...(analyticsMetadata || {}), ...(decisionId ? {decisionId} : {})} : undefined

        const slots = variation?.payload?.data?.slots || []
        const totalResults = variation?.payload?.data?.totalNumResults || 0
        const facets = variation?.payload?.data?.facets || []

        const carouselDisabled = categoryData?.c_dontShowTileImageCarousel

        // Messaggi statici per le opzioni di ordinamento
        const sortMessages = {
            priceAscending: {id: 'sort.label.priceAscending', defaultMessage: 'Price Low to High'},
            priceDescending: {id: 'sort.label.priceDescending', defaultMessage: 'Price High to Low'},
            newArrivals: {id: 'sort.label.newArrivals', defaultMessage: 'New Arrivals'},
            bestMatches: {id: 'sorting.plp.best_matches', defaultMessage: 'Recommended'},
        }

        return {
            hits: transformDynamicYieldProducts(slots, params, locale, hostLinkLocalized, carouselDisabled),
            total: totalResults,
            limit: params.limit,
            offset: params.offset,
            query: params.q,
            refinements: transformDynamicYieldFacets(facets, categoryData, intl),
            selectedSortingOption: params.sort || null,
            analyticsMetadata: analyticsMetadataWithDecision,
            requestedCategoryId: categoryId,
            sortingOptions: [
                {
                    id: 'price-ascending',
                    label: intl ? intl.formatMessage(sortMessages.priceAscending) : 'Price Low to High',
                },
                {
                    id: 'price-descending',
                    label: intl ? intl.formatMessage(sortMessages.priceDescending) : 'Price High to Low',
                },
                {
                    id: 'Online Timestamp',
                    label: intl ? intl.formatMessage(sortMessages.newArrivals) : 'New Arrivals',
                },
                {
                    id: 'best-matches',
                    label: intl ? intl.formatMessage(sortMessages.bestMatches) : 'Best Matches',
                },
            ],
        }
    } catch (error) {
        console.error('Error calling Dynamic Yield API:', error)
        throw error
    }
}

const useCustomSdk = ({
    type,
    urlSegment,
    params,
    inheritedPageId,
    inheritedCategory,
    userLoadMore,
    enabled = true,
}: CustomSdkHookParams): UseQueryResult<CustomSdkData> => {
    const api = useCommerceApi()
    const {locale, site} = useMultiSite()
    const {res} = useServerContext()
    const intl = useIntl()
    const [allProducts, setAllProducts] = useState<ProductSearchHits>([])
    const [cookies, setCookie] = useCookies(['_dyid_server', '_dyjsession', '_dyid'])

    const {app, customParams} = getConfig()
    const {appPreferences} = useGlobalConfigState()
    const siteId = app.commerceAPI.parameters.siteId
    const isProduction = customParams.isProduction
    const env = app.commerceAPI.parameters.organizationId

    const hostLinkLocalized = useMemo(() => {
        if (!appPreferences?.hostLink || !site?.id) return undefined
        return JSON.parse(appPreferences.hostLink)[site.id]
    }, [appPreferences?.hostLink, site?.id])

    const siteApi = useMemo(
        () =>
            new API({
                env,
                siteId,
                isProduction,
            }),
        [env, siteId, isProduction],
    )

    const aliasCountry = locale?.alias?.split('-')?.[1]?.toLowerCase()
    const {getTokenWhenReady} = useAccessToken()
    const queryKey = ['urlMapping', urlSegment]
    const inheritedPageIdWithSegment = inheritedPageId as PageIdWithUrlSegment | undefined

    const dyCookies = useMemo(
        () => ({
            _dyid_server: cookies._dyid_server,
            _dyid: cookies._dyid,
            _dyjsession: cookies._dyjsession,
        }),
        [cookies._dyid_server, cookies._dyid, cookies._dyjsession],
    )
    const consentAccepted = useMemo(() => {
        if (typeof document === 'undefined') return false
        const cookieStr = document.cookie || ''
        const cookieMap = cookieStr.split(';').reduce(
            (acc, entry) => {
                const [key, ...valArr] = entry.trim().split('=')
                acc[key] = valArr.join('=')
                return acc
            },
            {} as Record<string, string>,
        )

        if (!cookieMap.OptanonConsent) return false
        const optanon = decodeURIComponent(cookieMap.OptanonConsent)
        const groupsMatch = optanon.match(/groups=([^&]+)/)
        if (!groupsMatch) return false
        const groupsArray = groupsMatch[1].split(',')
        const consentCategoryGroup = groupsArray.find((group) => group.indexOf('C0004:') === 0)
        if (!consentCategoryGroup) return false
        return consentCategoryGroup.split(':')[1] === '1'
    }, [cookies])

    useEffect(() => {
        const clientDyId = cookies._dyid
        if (clientDyId && clientDyId !== cookies._dyid_server) {
            setCookie('_dyid_server', String(clientDyId), {
                path: '/',
                maxAge: 60 * 60 * 24 * 365,
            })
        }
    }, [cookies._dyid, cookies._dyid_server, setCookie])

    const categoryData: CategoryType | null = inheritedCategory ?? null
    const [exposedProductData, setExposedProductData] = useState<ProductSearchType | null>(null)
    const [exposedRouteMarker, setExposedRouteMarker] = useState<string | null>(null)
    const previousUrlSegmentRef = useRef<string>(urlSegment)

    const sdkResponse = useQuery<CustomSdkData>(
        queryKey,
        async () => {
            const token = await getTokenWhenReady()
            const pageIdData: PageIdWithUrlSegment | null = inheritedPageIdWithSegment ?? null
            let pageDesignerData: PageDesignerType | null = null
            let productData: ProductSearchType | null = null

            const headers = {
                authorization: `Bearer ${token}`,
            }
            if (!params.isSearch && pageIdData?.statusCode === '301') {
                res.status(pageIdData?.statusCode)
                res.set('Location', pageIdData.c_destinationUrl)
            }

            if ((pageIdData?.statusCode !== '301' && type === 'product' && categoryData) || params.isSearch) {
                let hasHiddenPrices = false
                let pageDesignerPromise, productSearchPromise
                if (params.isSearch) {
                    const customPrefRes = await siteApi.getCustomPreference('CountriesWithZeroPrices', token)
                    hasHiddenPrices = customPrefRes?.['CountriesWithZeroPrices']?.find((country) => country.toLowerCase() === aliasCountry)
                } else {
                    if (!categoryData) {
                        return {pageIdData, categoryData: null, productData: null, pageDesignerData: null}
                    }
                    hasHiddenPrices = categoryData.c_countriesWithZeroPrices
                    pageDesignerPromise = api.shopperExperience.getPages({
                        parameters: {
                            categoryId: categoryData.id,
                            locale: locale?.id,
                            aspectTypeId: 'plp',
                            aspectAttributes: `{"category": "${categoryData.id}"}`,
                        },
                        headers: headers,
                    })
                }
                if (hasHiddenPrices && params) {
                    if (params.refine) {
                        params.refine = params.refine.filter((filter) => !filter.startsWith('price='))
                    }
                }

                const limit = params.c_template === 'search/grid3Template' ? 21 : 20

                params.limit = categoryData?.c_pageSize ? categoryData?.c_pageSize : limit
                if (!params.refine) {
                    params.refine = []
                }
                if (params.offset === undefined) {
                    params.offset = 0
                }

                const limitValue = params.limit ?? 0
                const sizeValue = params.sz ?? 0
                const offsetValue = params.offset ?? 0
                const isFirstPage = sizeValue <= limitValue

                if (isFirstPage) {
                    params.offset = 0
                    setAllProducts([])
                }
                params.expand = 'prices'

                if (!params.isSearch && pageIdData?.resourceId) {
                    params?.refine?.push(`cgid=${pageIdData?.resourceId}`)
                } else if (!params.isSearch && userLoadMore.current) {
                    params?.refine?.push(`cgid=${categoryData?.id}`)
                }
                // Check if params.refine includes a string that starts with 'pmid='
                const hasPromotionFilterExplicitlyAdded = params.refine?.some((filter) => filter.startsWith('pmid=')) || params.isSearch
                if (!hasPromotionFilterExplicitlyAdded) {
                    const activePromo = categoryData?.c_promoNosale || categoryData?.c_promoNopreview
                    if (activePromo) {
                        params.refine.push(`pmid=${activePromo}`)
                    }
                } else {
                    if (params.isSearch && categoryData?.c_promoNopreview) {
                        params.refine.push(`pmid=${categoryData?.c_promoNopreview}`)
                    }
                }

                try {
                    if (!isFirstPage && offsetValue > 0 && !userLoadMore.current) {
                        productSearchPromise = fetchAndMergeProducts(
                            params,
                            locale,
                            categoryData?.id || pageIdData?.resourceId || null,
                            categoryData?.parentCategoryTree,
                            site.id,
                            categoryData,
                            dyCookies,
                            consentAccepted,
                            setCookie as (name: string, value: string, options: {path: string; maxAge: number}) => void,
                            intl,
                            hostLinkLocalized,
                        )
                    } else {
                        if (offsetValue > limitValue) {
                            params.offset = offsetValue - limitValue
                        }
                        productSearchPromise = dynamicYieldProductSearch(
                            params,
                            locale,
                            categoryData?.id || pageIdData?.resourceId || null,
                            categoryData?.parentCategoryTree,
                            site.id,
                            categoryData,
                            dyCookies,
                            consentAccepted,
                            setCookie as (name: string, value: string, options: {path: string; maxAge: number}) => void,
                            intl,
                            hostLinkLocalized,
                        )
                        if (userLoadMore?.current) {
                            userLoadMore.current = false
                        }
                    }

                    let pageDesignerResponse, productResponse
                    if (pageDesignerPromise) {
                        ;[pageDesignerResponse, productResponse] = await Promise.all([pageDesignerPromise, productSearchPromise])
                        pageDesignerData = pageDesignerResponse?.data[0]?.regions
                    } else {
                        productResponse = await Promise.resolve(productSearchPromise)
                    }

                    if (allProducts && allProducts?.length > 0 && productResponse?.hits && sizeValue > limitValue) {
                        const prevIds = new Set(allProducts?.map((p) => p?.productId))
                        const filteredHits = productResponse.hits.filter((p) => !prevIds.has(p.productId))
                        productResponse.hits = [...allProducts, ...filteredHits]
                    }

                    productData = productResponse

                    if (productData && !productData.selectedRefinements && Array.isArray(params.refine)) {
                        const selectedRefinements = params.refine.reduce(
                            (acc, refine) => {
                                const [attribute, ...rest] = refine.split('=')
                                const rawValue = rest.join('=')
                                if (!attribute || !rawValue) return acc

                                const value = rawValue.replace(/%26/g, '&')
                                acc[attribute] = acc[attribute] ? `${acc[attribute]}|${value}` : value
                                return acc
                            },
                            {} as Record<string, string>,
                        )

                        if (Object.keys(selectedRefinements).length > 0) {
                            productData.selectedRefinements = selectedRefinements
                        }
                    }

                    if (productData) {
                        ;(
                            productData as ProductSearchType & {
                                requestedUrlSegment?: string
                                requestedCategoryId?: string | null
                            }
                        ).requestedUrlSegment = urlSegment
                    }
                } catch (error) {
                    if (pageDesignerPromise) {
                        pageDesignerData = null
                        console.error('Error fetching product data or page designer data:', error)
                    } else {
                        console.error('Error fetching product data:', error)
                    }
                    productData = null
                    throw error
                }

                if (productData?.searchPhraseSuggestions?.c_searchRedirectStatus === 301) {
                    res.status(productData?.searchPhraseSuggestions?.c_searchRedirectStatus)
                    res.set('Location', productData?.searchPhraseSuggestions?.c_searchRedirectUrl)
                }
            }

            return {pageIdData, categoryData, productData, pageDesignerData}
        },
        {
            enabled,
            staleTime: 1000 * 10,
            cacheTime: 1000 * 60 * 3,
            keepPreviousData: true,
        },
    )

    useEffect(() => {
        if (previousUrlSegmentRef.current !== urlSegment) {
            setExposedProductData(null)
            setExposedRouteMarker(urlSegment)
            previousUrlSegmentRef.current = urlSegment
        }
    }, [urlSegment])

    useEffect(() => {
        if (sdkResponse.data?.productData?.hits) {
            const newHits = sdkResponse.data.productData.hits
            setAllProducts((prev) => {
                if ((params.offset ?? 0) > 0) {
                    const safePrev = prev ?? []
                    const prevIds = new Set(safePrev.map((p) => p?.productId))
                    const filteredHits = newHits.filter((p) => !prevIds.has(p.productId))
                    return [...(Array.isArray(prev) ? prev : []), ...(Array.isArray(filteredHits) ? filteredHits : [])]
                } else {
                    return newHits
                }
            })
        }
    }, [sdkResponse.data?.productData?.hits, params.offset])

    useEffect(() => {
        const productData = sdkResponse.data?.productData as
            | (ProductSearchType & {requestedUrlSegment?: string; requestedCategoryId?: string | null})
            | null
            | undefined

        if (!productData) return
        if (productData.requestedUrlSegment !== urlSegment) return

        setExposedProductData(productData)
        setExposedRouteMarker(urlSegment)
    }, [sdkResponse.data?.productData, urlSegment])

    const scopedData = useMemo(() => {
        if (!sdkResponse.data) return sdkResponse.data

        const shouldExposeCurrentProductData = exposedRouteMarker === urlSegment

        return {
            ...sdkResponse.data,
            productData: shouldExposeCurrentProductData ? exposedProductData : null,
        }
    }, [sdkResponse.data, exposedProductData, exposedRouteMarker, urlSegment])

    return {
        ...sdkResponse,
        data: scopedData,
    }
}

export {useCustomSdk}
